import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Star, Shield, Smile, Gift, Rocket } from "lucide-react";

const TEXT = {
  en: {
    title: "How to earn rewards",
    intro: "Every level has 20 questions. The better you do, the more you collect!",
    back: "Back",
    cards: [
      { Icon: Star, color: "#FFB020", title: "Stars", lines: ["⭐⭐⭐ for 100% on a level", "⭐⭐ for 70% or more", "⭐ for 40% or more", "Your best score on each level is kept — try again to win more stars!"] },
      { Icon: Shield, color: "#3D8BFF", title: "Shields", lines: ["Master a whole chapter to win a shield.", "Mastered = Easy, Medium and Difficult levels all scored 80% or more."] },
      { Icon: Smile, color: "#A66CFF", title: "Avatars", lines: ["Every 5 shields = 1 token to unlock a new avatar.", "Bonus: master at least 1 chapter in a week and get 1 extra token!", "Each grade has its own set of fun avatars to collect."] },
      { Icon: Gift, color: "#FF6B4A", title: "Surprise gift", lines: ["Stay subscribed 12 months in a row (the Holiday Pack counts as 2 months) and finish at least 80% of your syllabus to win a year-end gift!"] },
    ],
  },
  fr: {
    title: "Comment gagner des récompenses",
    intro: "Chaque niveau a 20 questions. Plus tu réussis, plus tu collectionnes !",
    back: "Retour",
    cards: [
      { Icon: Star, color: "#FFB020", title: "Étoiles", lines: ["⭐⭐⭐ pour 100 % à un niveau", "⭐⭐ pour 70 % ou plus", "⭐ pour 40 % ou plus", "Ton meilleur score est gardé — réessaie pour gagner plus d'étoiles !"] },
      { Icon: Shield, color: "#3D8BFF", title: "Boucliers", lines: ["Maîtrise un chapitre entier pour gagner un bouclier.", "Maîtrisé = niveaux Facile, Moyen et Difficile tous réussis à 80 % ou plus."] },
      { Icon: Smile, color: "#A66CFF", title: "Avatars", lines: ["5 boucliers = 1 jeton pour débloquer un nouvel avatar.", "Bonus : maîtrise au moins 1 chapitre dans la semaine et gagne 1 jeton en plus !", "Chaque grade a sa propre collection d'avatars."] },
      { Icon: Gift, color: "#FF6B4A", title: "Cadeau surprise", lines: ["Apprends avec Anou Appran pendant une année complète et reçois un cadeau surprise !"] },
    ],
  },
};

export default function Rewards() {
  const navigate = useNavigate();
  const [lang, setLang] = useState("en");
  const t = TEXT[lang];
  return (
    <div className="min-h-screen bg-white font-parent text-slate-900">
      <header className="max-w-4xl mx-auto px-5 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-blue-600 border-2 border-slate-900 flex items-center justify-center shadow-[2px_2px_0_0_#0F172A]"><Rocket color="#fff" size={18} /></div>
          <span className="font-kids text-lg font-bold">Anou Appran</span>
        </Link>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-full border-2 border-slate-900 p-0.5" data-testid="rewards-lang">
            {["en", "fr"].map((l) => (
              <button key={l} onClick={() => setLang(l)} data-testid={`rewards-lang-${l}`}
                className={`px-3 py-1 rounded-full text-xs font-bold ${lang === l ? "bg-slate-900 text-white" : ""}`}>{l.toUpperCase()}</button>
            ))}
          </div>
          <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm font-semibold" data-testid="rewards-back"><ArrowLeft size={16} /> {t.back}</button>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-5 pb-16">
        <div className="rounded-[32px] bg-[#1E5EFF] text-white p-8 sm:p-10 mt-2">
          <h1 className="font-kids text-4xl sm:text-5xl font-black" data-testid="rewards-title">{t.title}</h1>
          <p className="mt-3 text-blue-50 text-lg">{t.intro}</p>
        </div>
        <div className="grid sm:grid-cols-2 gap-5 mt-8">
          {t.cards.map((c, i) => (
            <motion.div key={c.title} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}
              className="rounded-3xl bg-white p-6 shadow-[0_12px_28px_rgba(31,42,68,0.1)] border border-slate-100" data-testid={`rewards-card-${i}`}>
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: c.color }}><c.Icon color="#fff" size={28} /></div>
              <h2 className="font-kids text-2xl font-bold mt-4">{c.title}</h2>
              <ul className="mt-3 space-y-2">
                {c.lines.map((l) => <li key={l} className="text-slate-700 leading-relaxed">{l}</li>)}
              </ul>
            </motion.div>
          ))}
        </div>
      </main>
    </div>
  );
}
